export const WEAK_WORDS = [
  "seeking",
  "wanting",
  "looking for",
  "in search of",
  "searching for",
  "on the hunt for",
  "trying to find",
  "would like",
  "hoping for",
  "iso",
  "in need of",
  "desire",
];

const LOT_LAND_TYPE = 6;

const PROPERTY_TYPE_WORDS = {
  1: ["house", "home", "single", "residential", "ranch"],
  2: ["condo", "condominium"],
  3: ["townhouse", "townhome", "townhome"],
  4: ["multi", "duplex", "triplex", "fourplex", "unit"],
  5: ["manufactured", "mobile"],
  6: ["land", "lot", "acre"],
};

const WEAK_WORD_RE = new RegExp(
  "\\b(" +
    WEAK_WORDS.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|") +
    ")\\b",
  "i"
);

const hasWeakWord = (text) => (text ? WEAK_WORD_RE.test(String(text)) : false);

const cat = (id, label, earned, max, doneAt, hint) => ({
  id,
  label,
  earned: Math.round(earned),
  max,
  done: earned >= doneAt,
  hint,
});

const hasPreciseArea = (d) =>
  Boolean(
    d?.zipCode?.length ||
      d?.zipIdValues?.length ||
      d?.zipId?.length ||
      d?.drawnPolygon ||
      (d?.lat && d?.lng && d?.mapRadius)
  );

const scoreLocation = (d) => {
  const hasArea = d?.location || (d?.city && d?.state);
  const areaText = hasArea ? 4 : d?.city || d?.state ? 2 : 0;
  const precise = hasPreciseArea(d) ? 14 : 0;
  const refine = d?.neighborHood || d?.highSchool ? 4 : 0;
  const earned = areaText + precise + refine;
  let hint = "Add the buyer's target area";
  if (!precise) hint = "Set the search area - ZIP codes or a map radius";
  else if (!areaText) hint = "Add the city and state";
  else if (!refine) hint = "Optional: name a neighborhood or school to focus it";
  return cat("location", "Location & area", earned, 22, 20, hint);
};

const scoreBudget = (d) => {
  const earned = (d?.maxBdgt ? 6 : 0) + (d?.minBdgt ? 4 : 0);
  let hint = "Set a budget range";
  if (!d?.maxBdgt) hint = "Set a max budget";
  else if (!d?.minBdgt) hint = "Add a min budget for a full range";
  return cat("budget", "Budget range", earned, 10, 10, hint);
};

const scoreBasics = (d) => {
  const isLand = Number(d?.prprtyType) === LOT_LAND_TYPE;
  const type = d?.prprtyType ? 4 : 0;
  const sub = d?.prptySubTypes?.length ? 4 : 0;
  let rest;
  let hint;
  if (isLand) {
    rest = d?.minLtSz || d?.maxLtSz ? 10 : 0;
    hint = !type
      ? "Choose a property type"
      : !sub
      ? "Pick property sub-types"
      : "Add the lot size";
  } else {
    rest = (d?.noBedRm ? 5 : 0) + (d?.noBathRm ? 5 : 0);
    hint = !type
      ? "Choose a property type"
      : !sub
      ? "Pick property sub-types"
      : !d?.noBedRm
      ? "Add bedrooms"
      : !d?.noBathRm
      ? "Add bathrooms"
      : "Property basics look good";
  }
  const earned = type + sub + rest;
  return cat("basics", "Property basics", earned, 18, 16, hint);
};

const scoreDetails = (d) => {
  const isLand = Number(d?.prprtyType) === LOT_LAND_TYPE;
  let earned;
  let hint;
  if (isLand) {
    const bothLot = d?.minLtSz && d?.maxLtSz;
    earned = bothLot ? 14 : d?.minLtSz || d?.maxLtSz ? 7 : 0;
    hint = "Add a lot-size range";
  } else {
    const sqft = d?.sqFtMin && d?.sqFtMax ? 6 : d?.sqFtMin || d?.sqFtMax ? 3 : 0;
    const garage = d?.grgSpce ? 3 : 0;
    const stories = d?.stories ? 2 : 0;
    const year = d?.yrBltMin ? 3 : 0;
    earned = sqft + garage + stories + year;
    hint = !sqft
      ? "Add a square-footage range"
      : !garage
      ? "Add garage spaces"
      : !year
      ? "Add a year-built preference"
      : "Add a stories preference";
  }
  return cat("details", "Size & features", earned, 14, 12, hint);
};

const scoreNiceToHaves = (d) => {
  const list = Array.isArray(d?.nice2Have) ? d.nice2Have : [];
  const n = list.length;
  const earned = (Math.min(6, n) * 14) / 6;
  const hint =
    n === 0
      ? "Add nice-to-haves - blank ones match every listing"
      : `Add ${6 - n} more nice-to-have${6 - n > 1 ? "s" : ""} to max this out`;
  return cat("niceToHaves", "Nice-to-haves", earned, 14, 14, hint);
};

const scoreBuyer = (d) => {
  const timeline = d?.timeLine || d?.moveInTimeLine ? 3 : 0;
  const financing =
    d?.isPreApprLoan || d?.creditScore || d?.pFinance || d?.purchaseType ? 2 : 0;
  const client = d?.clientId ? 2 : 0;
  const earned = timeline + financing + client;
  let hint = "Add the buyer's timeline";
  if (!timeline) hint = "Add the buyer's timeline";
  else if (!financing) hint = "Add financing detail (pre-approved, cash, etc.)";
  else if (!client) hint = "Attach the buyer client";
  return cat("buyer", "Buyer & timeline", earned, 7, 6, hint);
};

const featureKeywords = (d) =>
  (Array.isArray(d?.nice2Have) ? d.nice2Have : [])
    .map((x) =>
      String(x?.label || "")
        .toLowerCase()
        .split(/[ /(]/)[0]
    )
    .filter((w) => w.length > 3);

const specificitySignals = (text, d, keywords) => {
  const t = String(text || "").toLowerCase();
  let hits = 0;
  const typeWords = PROPERTY_TYPE_WORDS[Number(d?.prprtyType)] || [];
  if (typeWords.some((w) => t.includes(w))) hits += 1;
  if (d?.city && t.includes(String(d.city).toLowerCase())) hits += 1;
  if (/\d/.test(t)) hits += 1;
  if (/\$|\dk|,000|under|below|budget/.test(t)) hits += 1;
  if (keywords.some((w) => t.includes(w))) hits += 1;
  return hits;
};

const scorePolish = (d) => {
  const keywords = featureKeywords(d);

  const title = (d?.adTitle || "").trim();
  const tl = title.length;
  const tLen = tl >= 30 ? 2 : tl > 0 ? 1 : 0;
  const tSpec = tl > 0 ? Math.min(4, specificitySignals(title, d, keywords)) : 0;
  const titleP = tLen + tSpec;

  const desc = (d?.adDesc || "").trim();
  const dl = desc.length;
  const dPresent = dl > 0 ? 1 : 0;
  const dDepth = dl >= 220 ? 2 : dl >= 120 ? 1.5 : dl >= 50 ? 0.75 : 0;
  let cov = 0;
  const dt = desc.toLowerCase();
  if (d?.city && dt.includes(String(d.city).toLowerCase())) cov += 1;
  if (keywords.some((w) => dt.includes(w))) cov += 1;
  if ((desc.match(/[.!?]/g) || []).length >= 3) cov += 1;
  const dCov = dl > 0 ? Math.min(2, cov * 0.7) : 0;
  const descP = dPresent + dDepth + dCov;

  const synd = d?.syndicateToThePublic ? 2 : 0;
  const media = d?.videoUrl || d?.thumbnailImgUrl ? 2 : 0;
  const earned = titleP + descP + synd + media;

  let hint = "Write a clear, specific title and description";
  if (tl === 0)
    hint = "Add a title - name what the buyer wants, the area, and a feature";
  else if (tSpec < 3)
    hint = "Make the title clearer - the property, area, beds, or price";
  else if (descP < 3)
    hint = "Add 2-3 sentences: the area, the must-haves, and the buyer's goal";
  else if (!synd) hint = "Syndicate to reach agents beyond your MLS";
  else if (!media) hint = "Optional: add a video to stand out";
  return cat("polish", "Title, description & reach", earned, 15, 13, hint);
};

export const calculateOptimizationScore = (wantAdData) => {
  const d = wantAdData || {};
  const breakdown = [
    scoreLocation(d),
    scoreBudget(d),
    scoreBasics(d),
    scoreDetails(d),
    scoreNiceToHaves(d),
    scoreBuyer(d),
    scorePolish(d),
  ];
  const score = Math.max(
    0,
    Math.min(100, Math.round(breakdown.reduce((s, b) => s + b.earned, 0)))
  );
  return { score, breakdown };
};

export const scoreTier = (score) => {
  if (score >= 76) return "strong";
  if (score >= 50) return "fair";
  return "weak";
};

export const isScoreActive = (wantAdData) => {
  const d = wantAdData || {};
  const hasType = Boolean(d.prprtyType);
  const hasLocation = Boolean(
    d.location ||
      d.city ||
      d.zipCode?.length ||
      d.zipIdValues?.length ||
      d.zipId?.length ||
      (d.lat && d.lng) ||
      d.drawnPolygon
  );
  const hasBudget = Boolean(d.maxBdgt || d.minBdgt);
  return hasType && hasLocation && hasBudget;
};
